/**
 * AsProjectsList Component
 *
 * Container that renders the project list:
 * - Always shows current project first (with `current` attribute)
 * - Shows saved projects below
 * - Shows "No projects available" when empty
 *
 * ## Events Listened (on window)
 * - `as-projects-list`: Updates saved projects list - `{ projects: Project[] }`
 *
 * ## Events Dispatched
 * - `as-project-load-request`: Requests saved projects - `{ appId: string }`
 *
 * ## Usage
 * ```html
 * <as-projects-list></as-projects-list>
 * ```
 */
import './as-project-item.ts';
import { liveAppData } from '../../store/store.ts';
import type { Project } from "../../data/projectManagement.ts";
import { isProjectUnsaved, isProjectDownloaded } from '../../data/projectManagement.ts';
import styleText from './as-projects-list.css?raw';
import flashStyle from '../../styles/flash.css?raw';
import { handleFlashEvent } from '../../styles/flash.ts';

export class AsProjectsList extends HTMLElement {
  private static readonly sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();

  private static readonly flashSheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(flashStyle);
    return sheet;
  })();

  private savedProjects: Project[] = [];
  private boundHandleProjectsUpdate: (event: CustomEvent) => void;
  private boundHandleFlash: (event: Event) => void;
  private removeSideEffect: (() => void) | null = null;

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [AsProjectsList.sheet, AsProjectsList.flashSheet];
    this.boundHandleProjectsUpdate = this.handleProjectsUpdate.bind(this);
    this.boundHandleFlash = this.handleFlash.bind(this);
    this.render();
  }

  connectedCallback(): void {
    // Listen for projects list updates from as-app
    window.addEventListener('as-projects-list', this.boundHandleProjectsUpdate as EventListener);

    // Listen for flash requests
    window.addEventListener('as-flash-element', this.boundHandleFlash);

    // Subscribe to store changes for currentProject
    this.removeSideEffect = liveAppData.addSideEffect('currentProject', () => {
      this.render();
    });

    // Request saved projects from as-app
    this.requestProjectsList();
  }

  disconnectedCallback(): void {
    window.removeEventListener('as-projects-list', this.boundHandleProjectsUpdate as EventListener);
    window.removeEventListener('as-flash-element', this.boundHandleFlash);
    if (this.removeSideEffect) {
      this.removeSideEffect();
      this.removeSideEffect = null;
    }
  }

  private handleFlash(event: Event): void {
    handleFlashEvent(this, 'as-projects-list', event);
  }

  private requestProjectsList(): void {
    const appId = liveAppData.appId;
    if (appId) {
      window.dispatchEvent(new CustomEvent('as-project-load-request', {
        detail: { appId }
      }));
    }
  }

  private handleProjectsUpdate(event: CustomEvent): void {
    const { projects } = event.detail || {};
    if (Array.isArray(projects)) {
      this.savedProjects = projects;
      this.render();
    }
  }

  private render(): void {
    const currentProject = liveAppData.currentProject as Project | null;
    const hasCurrentProject = !!currentProject;
    const hasSavedProjects = this.savedProjects.length > 0;
    const hasAnyProjects = hasCurrentProject || hasSavedProjects;

    if (!hasAnyProjects) {
      this.shadowRoot!.innerHTML = /*html*/`
        <div class="projects-list">
          <p class="empty-message">No projects available</p>
        </div>
      `;
      return;
    }

    let html = '<div class="projects-list">';

    // Current project always shown first
    if (currentProject) {
      const unsavedAttr = isProjectUnsaved(currentProject) ? 'unsaved' : '';
      const undownloadedAttr = !isProjectDownloaded(currentProject) ? 'undownloaded' : '';
      html += /*html*/`
        <as-project-item
          project-id="${currentProject.metadata.id}"
          project-name="${currentProject.metadata.name}"
          current
          ${unsavedAttr}
          ${undownloadedAttr}
        ></as-project-item>
      `;
    }

    // Show saved projects if any (excluding current)
    const otherProjects = this.savedProjects.filter(
      p => p.metadata.id !== currentProject?.metadata.id
    );

    if (otherProjects.length > 0) {
      if (currentProject) {
        html += '<hr class="section-divider">';
        html += '<p class="section-label">Saved Projects</p>';
      }

      for (const project of otherProjects) {
        const unsavedAttr = isProjectUnsaved(project) ? 'unsaved' : '';
        const undownloadedAttr = !isProjectDownloaded(project) ? 'undownloaded' : '';
        html += /*html*/`
          <as-project-item
            project-id="${project.metadata.id}"
            project-name="${project.metadata.name}"
            ${unsavedAttr}
            ${undownloadedAttr}
          ></as-project-item>
        `;
      }
    }

    html += '</div>';
    this.shadowRoot!.innerHTML = html;
  }
}

if (!customElements.get('as-projects-list')) {
  customElements.define('as-projects-list', AsProjectsList);
} else {
  console.warn('as-projects-list already defined');
}
